/**
 * usePaymentCollectionDayAdapter — la machine de « Ma date de prélèvement » → les props de
 * `<PaymentCollectionDayFlow>`.
 *
 * Le geste est contrôlé : l'étape, le jour choisi, l'envoi et l'erreur vivent ici, pas dans le
 * gabarit. L'appel est celui de `ChangePaymentCollectionDayModal.js:93-100` — même service, même
 * corps `{ day, email }` — et c'est la seule écriture de ce parcours.
 *
 * L'écriture passe par `useApiMutation` (`ports/CLAUDE.md` § write-side) : une seule écriture,
 * aucune garde de plus — `skipWhilePending` tranche le double appui, que `submitting` ne voit
 * qu'au rendu suivant. Le `day` échoue rarement mais il échoue : la confirmation reste alors
 * ouverte, avec sa raison, et le geste encore possible.
 *
 * **Un seul parcours tant qu'un envoi est en vol**, comme la pause (`usePauseSubscriptionFlow`).
 * Fermer pendant l'envoi ne fait que CACHER la confirmation : le jour est parti, sa réponse doit
 * encore être lue. La rouvrir avant la réponse la remontre telle quelle, bouton en chargement. Le
 * succès s'accuse toujours par le `<Snackbar>` de l'écran (`onSuccess`), feuille visible ou non ;
 * un refus feuille cachée passe par le bandeau (`notice`) et reste dans la confirmation, que le
 * même élève retrouve en rouvrant. La fermeture n'est jamais verrouillée.
 */
import { useCallback, useReducer } from "react"

import { PAYMENT_COLLECTION_DAYS } from "../../../../data/constants/studentsConstants"
import { studentsService } from "../../../../data/services/studentsService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import { elide } from "../formatters/childFormatter"
import { useApiMutation } from "./useApiMutation"
import {
    type PaymentCollectionDayFlowProps,
    type PaymentCollectionDayStep,
} from "../../../../design-system/templates/PaymentCollectionDayFlow/PaymentCollectionDayFlow"

/** Ce que la réponse porte d'utile à l'accusé — le réducteur `currentUser` lit le reste. */
type SavedUser = { payment_collection_day?: number; firstname?: string } | undefined

export type PaymentCollectionDayConfig = {
    /** L'élève affiché : un refus gardé feuille cachée ne se remontre qu'à lui. */
    studentId: string | number | null
    /** Adresse de l'élève — le corps de la requête la porte, comme le legacy. */
    email?: string | null
    /** Jour actuellement prélevé. */
    currentDay?: number | null
    /** Prénom de l'élève — la confirmation le nomme. */
    studentFirstName?: string | null
    /** Accusé « c'est fait », remonté à l'écran pour son `<Snackbar>`. */
    onSuccess?: (day: number, message: string) => void
}

export type PaymentCollectionDayAdapter = {
    /** Ouvre la feuille, sur une page blanche — ou sur l'envoi en vol, tel quel. */
    open: () => void
    /** Les props de `<PaymentCollectionDayFlow>`, prêtes à être étalées. */
    flowProps: PaymentCollectionDayFlowProps
    /** Le bandeau de l'écran : le refus d'un envoi dont la feuille était cachée à la réponse. */
    notice: SnackbarProps
}

/** Repli quand le serveur n'explique pas son refus. */
const FAILED_MESSAGE = "Nous n'avons pas pu enregistrer cette date. Réessayez dans un instant."

type PaymentCollectionDayFlowState = {
    visible: boolean
    step: PaymentCollectionDayStep
    pendingDay: number | null
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    errorMessage: string | null
    /** Le bandeau de l'écran — le refus d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /** L'élève du parcours, pris à l'ouverture : un refus gardé ne se remontre qu'à lui. */
    studentId: string | number | null
}

const initialPaymentCollectionDayFlowState: PaymentCollectionDayFlowState = {
    visible: false,
    step: "days",
    pendingDay: null,
    submitting: false,
    errorMessage: null,
    notice: null,
    studentId: null,
}

type PaymentCollectionDayFlowAction =
    | { type: "OPEN"; studentId?: string | number | null }
    | { type: "CLOSE" }
    | { type: "SELECT_DAY"; day: number }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS" }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "DISMISS_NOTICE" }

/** Un parcours remis à zéro : seul le bandeau survit, il parle d'un envoi déjà joué. */
function resetFlow(state: PaymentCollectionDayFlowState): PaymentCollectionDayFlowState {
    return { ...initialPaymentCollectionDayFlowState, notice: state.notice }
}

function paymentCollectionDayFlowReducer(
    state: PaymentCollectionDayFlowState,
    action: PaymentCollectionDayFlowAction
): PaymentCollectionDayFlowState {
    switch (action.type) {
        case "OPEN": {
            const studentId = action.studentId ?? null
            // Un envoi en vol : c'est SA confirmation qu'on remontre, telle quelle. Un refus arrivé
            // feuille cachée aussi — il se lit dans la confirmation —, tant que c'est le même élève.
            const refusalKept = !state.visible && state.errorMessage != null && state.studentId === studentId
            if (state.submitting || refusalKept) return { ...state, visible: true }
            return { ...resetFlow(state), visible: true, studentId }
        }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return resetFlow(state)

        case "SELECT_DAY":
            return { ...state, pendingDay: action.day, errorMessage: null, step: "confirm" }

        case "SUBMIT":
            return { ...state, submitting: true, errorMessage: null }

        case "SUBMIT_SUCCESS":
            // L'accusé est le `<Snackbar>` de l'écran (`onSuccess`), feuille visible ou non.
            return resetFlow(state)

        case "SUBMIT_ERROR": {
            // Le refus reste dans la confirmation, jour compris. Feuille cachée, le bandeau le dit en
            // plus — sinon le parent croirait sa date changée.
            const refused = { ...state, submitting: false, errorMessage: action.message }
            return state.visible ? refused : { ...refused, notice: { message: action.message, tone: "danger" } }
        }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

export function usePaymentCollectionDayAdapter(config: PaymentCollectionDayConfig): PaymentCollectionDayAdapter {
    const { studentId, email, currentDay, studentFirstName, onSuccess } = config
    const [state, dispatchUi] = useReducer(paymentCollectionDayFlowReducer, initialPaymentCollectionDayFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    // Fermer n'abandonne pas l'écoute : la garde d'attente de la mutation reste levée jusqu'à la
    // réponse, et `state.pendingDay` ne bouge pas tant que l'envoi est en vol.
    const { start, gesture } = useApiMutation({
        service: studentsService.studentChangePaymentCollectionDay,
        gesture: "payment.collection_day",
        fallback: FAILED_MESSAGE,
        skipWhilePending: true,
        onSuccess: (data) => {
            const saved = data as SavedUser
            const day = saved?.payment_collection_day ?? state.pendingDay
            const name = saved?.firstname?.trim() || ""
            // Le legacy ouvrait un `DialogM` « Opération réussie » : un accusé ne se clique pas,
            // il se lit — c'est un `<Snackbar>` côté écran.
            if (day != null) {
                onSuccess?.(
                    day,
                    name
                        ? `Le prélèvement mensuel ${elide(name)} est désormais fixé au ${day} du mois.`
                        : `Votre prélèvement mensuel est désormais fixé au ${day} du mois.`
                )
            }
            dispatchUi({ type: "SUBMIT_SUCCESS" })
        },
        onError: (message) => dispatchUi({ type: "SUBMIT_ERROR", message }),
    })

    const open = useCallback(() => {
        gesture.opened()
        dispatchUi({ type: "OPEN", studentId })
    }, [studentId, gesture])
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])
    const selectDay = useCallback((day: number) => dispatchUi({ type: "SELECT_DAY", day }), [])

    const confirm = useCallback(() => {
        if (state.pendingDay == null) return
        if (start({}, { day: state.pendingDay, email: email ?? undefined })) dispatchUi({ type: "SUBMIT" })
    }, [email, start, state.pendingDay])

    return {
        open,
        notice: {
            visible: state.notice != null,
            message: state.notice?.message ?? "",
            tone: state.notice?.tone ?? "danger",
            onDismiss: dismissNotice,
        },
        flowProps: {
            visible: state.visible,
            step: state.step,
            // La liste que la modale legacy et la validation serveur partagent.
            days: PAYMENT_COLLECTION_DAYS,
            pendingDay: state.pendingDay,
            currentDay,
            studentFirstName,
            submitting: state.submitting,
            errorMessage: state.errorMessage,
            onClose: close,
            onSelectDay: selectDay,
            onConfirm: confirm,
            onCancelConfirm: close,
        },
    }
}
